import React, { useState } from "react";
import { Link } from "react-router-dom";
import { blogs } from "../data/blogsData";
import { Helmet } from "react-helmet-async";
import { useEffect } from "react";

const Blog = () => {
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");

  const categories = ["All", ...new Set(blogs.map(b => b.category))];

  const filteredBlogs = blogs.filter((blog) => {
    const searchText = search.toLowerCase();

    const contentText = blog.content
      ? blog.content.map(c => c.text || "").join(" ").toLowerCase()
      : "";

    return (
      blog.title.toLowerCase().includes(searchText) ||
      blog.category.toLowerCase().includes(searchText) ||
      blog.desc.toLowerCase().includes(searchText) ||
      contentText.includes(searchText)
    );
  });

  const categoryFiltered =
    activeCategory === "All"
      ? filteredBlogs
      : filteredBlogs.filter(b => b.category === activeCategory);

  const featured =
    categoryFiltered.find(b => b.featured) || categoryFiltered[0];

  const restBlogs = categoryFiltered.slice(1);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  

  return (
    <div className="bg-[#f7f8fc] min-h-screen">
      {/* SEO */}
      
      <Helmet>
        <title>Soft Coders Blog | IT Solutions & Software Insights</title>
        <meta
          name="description"
          content="Read Soft Coders blogs on software development, websites, and digital business solutions."
        />
        <link rel="canonical" href="https://softcoders.in/blog" />
      </Helmet>

      {/* ===== HERO HEADING ===== */}
      <div className="max-w-3xl mx-auto text-center px-4 mb-14">

        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 leading-tight">
          Soft Coders Blog
        </h1>

        <p className="mt-3 text-gray-500 text-sm sm:text-base">
          Insights, ideas and updates from our team on software, design & digital growth.
        </p>

        <div className="w-16 h-1 bg-blue-600 mx-auto mt-4 rounded-full"></div>
      </div>

      {/* ===== TOP SECTION ===== */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[1fr_320px] gap-10">

        {/* ===== FEATURED BLOG ===== */}
        {featured && (
          <div>
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <img
                src={featured.image || "/images/default.jpg"}
                alt={featured.title}
                className="w-full h-[220px] sm:h-[320px] object-cover"
              />
            </div>

            <div className="mt-6">
              <h2 className="text-2xl sm:text-3xl font-semibold text-gray-800">
                {featured.title}
              </h2>

              <p className="text-blue-600 mt-2 text-sm">
                {featured.category}
              </p>

              <p className="text-gray-500 text-sm mt-1">
                {featured.date} • {featured.readTime}
              </p>

              <p className="text-gray-600 mt-3 leading-relaxed">
                {featured.desc}
              </p>

              <Link
                to={`/blog/${featured.slug}`}
                className="inline-block mt-4 text-blue-600 font-semibold"
              >
                Read full blog →
              </Link>
            </div>
          </div>
        )}

        {/* ===== SIDEBAR ===== */}
        <div className="space-y-6">

          {/* SEARCH */}
          <div className="bg-white p-4 rounded-2xl shadow-sm">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search blog..."
              className="w-full px-4 py-3 rounded-xl border outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* POPULAR POSTS */}
          <div className="bg-white p-5 rounded-2xl shadow-sm">
            <h3 className="text-lg font-semibold mb-4">
              Popular Posts
            </h3>

            <div className="space-y-4">
              {blogs.slice(0, 4).map((post) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className="flex gap-3 group"
                >
                  <img
                    src={post.image || "/images/default.jpg"}
                    alt={post.title}
                    className="w-20 h-16 object-cover rounded-lg"
                  />

                  <div>
                    <p className="text-sm font-medium group-hover:text-blue-600">
                      {post.title}
                    </p>
                    <p className="text-xs text-gray-500">
                      {post.date}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>

        </div>
      </div>

      {/* ===== CATEGORY FILTER ===== */}
      <div className="max-w-7xl mx-auto px-4 mt-16">
        <div className="flex flex-wrap gap-3 justify-center mb-10">
          {categories.map((cat, i) => (
            <button
              key={i}
              onClick={() => setActiveCategory(cat)}
              className={`px-5 py-2 rounded-full text-sm ${
                activeCategory === cat
                  ? "bg-blue-600 text-white"
                  : "bg-white text-gray-600"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* ===== ALL BLOGS ===== */}
      <div className="max-w-7xl mx-auto px-4 pb-20">
        <div className="grid md:grid-cols-2 gap-8">

          {restBlogs.map((blog) => (
            <div
              key={blog.id}
              className="bg-white rounded-2xl shadow-sm overflow-hidden hover:shadow-md transition"
            >
              <img
                src={blog.image || "/images/default.jpg"}
                alt={blog.title}
                className="h-56 w-full object-cover"
              />

              <div className="p-5">
                <h3 className="text-lg font-semibold text-gray-800">
                  {blog.title}
                </h3>

                <p className="text-blue-600 text-sm mt-2">
                  {blog.category}
                </p>

                <p className="text-gray-500 text-sm">
                  {blog.date}
                </p>

                <p className="text-gray-600 mt-3 text-sm">
                  {blog.desc}
                </p>

                <Link
                  to={`/blog/${blog.slug}`}
                  className="inline-block mt-4 text-blue-600 font-semibold text-sm"
                >
                  Read more →
                </Link>
              </div>
            </div>
          ))}

        </div>
      </div>

    </div>
  );
};

export default Blog;